import { InlineMessage, Spinner, Stack, type StatusStep, StatusTimeline, Text, TextLink } from '@olytic/foundation';
import { useEffect, useState } from 'react';

import { authenticatedFetch } from '../auth/authClient';
import { FIELD_FOR_ITEM, PRODUCT_TABS, TAB_FOR_FIX } from '../config/objects/products';
import { loadReadiness, type Readiness, type ReadinessItem, readinessSteps } from './productSetup';

/**
 * Shows one of the record page's tabs, by its label. ObjectRecordPage keeps
 * its active tab to itself, so this does what a click would: the tab's own
 * trigger, or the More menu's item when the tab row has overflowed.
 */
export function showRecordTab(label: string): void {
  const tabs = [...document.querySelectorAll<HTMLElement>('main [role="tab"]')];
  const tab = tabs.find((t) => t.textContent?.trim() === label);
  if (tab) {
    tab.dispatchEvent(new MouseEvent('mousedown', { bubbles: true, cancelable: true, button: 0 }));
    tab.focus();
    tab.scrollIntoView({ block: 'nearest' });
    return;
  }
  const more = document.querySelector<HTMLElement>('main button[aria-label^="More tabs"]');
  if (!more) return;
  more.dispatchEvent(new PointerEvent('pointerdown', { bubbles: true, cancelable: true, button: 0, pointerType: 'mouse' }));
  window.requestAnimationFrame(() => {
    const item = [...document.querySelectorAll<HTMLElement>('[role="menuitemradio"]')].find((i) => i.textContent?.trim() === label);
    item?.click();
  });
}

/** Opens a Details field's own editor, by its label (as the palette's "Edit …" does). */
function editRecordField(label: string): void {
  showRecordTab(PRODUCT_TABS.setup);
  window.requestAnimationFrame(() => {
    const button = [...document.querySelectorAll<HTMLButtonElement>('main button[aria-label]')].find(
      (b) => b.getAttribute('aria-label') === `Edit ${label}`,
    );
    button?.scrollIntoView({ block: 'center' });
    button?.click();
  });
}

/** The link beside a missing item: its own field, else the tab that fixes it. */
function fixLink(item: ReadinessItem): StatusStep['detail'] {
  const field = item.item ? FIELD_FOR_ITEM[item.item] : undefined;
  if (field) {
    return (
      <TextLink href="#setup" onClick={(event) => { event.preventDefault(); editRecordField(field); }}>
        Edit {field.toLowerCase()}
      </TextLink>
    );
  }
  const tab = item.fix ? TAB_FOR_FIX[item.fix] : undefined;
  if (!tab || tab === 'setup') return undefined;
  const label = PRODUCT_TABS[tab];
  return (
    <TextLink href={`#${tab}`} onClick={(event) => { event.preventDefault(); showRecordTab(label); }}>
      Go to {label}
    </TextLink>
  );
}

/** A product's readiness, reloaded whenever `version` changes (the record page reloads it after every edit). */
export function useProductReadiness(id: string, enabled: boolean, version: number): { readiness: Readiness | null; error: string | null } {
  const [readiness, setReadiness] = useState<Readiness | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (!enabled) return undefined;
    const controller = new AbortController();
    setError(null);
    loadReadiness(authenticatedFetch, id, controller.signal)
      .then((r) => { if (!controller.signal.aborted) setReadiness(r); })
      .catch((e: unknown) => { if (!controller.signal.aborted) setError(e instanceof Error ? e.message : 'Could not load the checklist.'); });
    return () => controller.abort();
  }, [id, enabled, version]);
  return { readiness, error };
}

/**
 * The Setup tab's checklist: what ops.product_readiness() says the product
 * still needs, each item linked to where it is fixed. Blocking items stop
 * Activate; warnings are worth finishing first.
 */
export function ProductSetupChecklist({ readiness, error, status }: { readiness: Readiness | null; error: string | null; status: string }): JSX.Element {
  const blocking = readiness?.items.filter((i) => i.blocking).length ?? 0;
  const summary =
    status === 'retired'
      ? 'Retired: not quoted. Activate it again to sell it.'
      : status === 'active'
        ? blocking > 0
          ? 'Active and quoted, though it was activated before these checks: finish what is missing.'
          : 'Active and quoted.'
        : blocking > 0
          ? `A draft, not quoted yet: ${blocking === 1 ? 'one thing blocks' : `${blocking} things block`} activating it.`
          : 'A draft, ready to activate.';
  return (
    <section aria-label="Setup checklist">
      <Stack gap="sm">
        <Text as="h3" size="md">Setup checklist</Text>
        <Text tone="muted">{summary}</Text>
        {error ? <InlineMessage tone="error" role="alert">{error}</InlineMessage> : null}
        {readiness ? <StatusTimeline steps={readinessSteps(readiness, fixLink)} /> : error ? null : <Spinner label="Checking the product…" />}
      </Stack>
    </section>
  );
}
